import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-navy-light py-8 text-center text-white/60 text-sm" data-id="element-90">
        <p className="mb-3" data-id="element-91">
          © {new Date().getFullYear()} The Screen-Free Sanity Playbook. All
          rights reserved.
        </p>
        <div className="flex items-center justify-center gap-4" data-id="element-92">
          <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-93">
            Privacy Policy
          </button>
          <span data-id="element-94">•</span>
          <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-white transition-colors" data-id="element-95">
            Terms of Service
          </button>
        </div>
      </footer>

      {/* Modal Overlay */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setActiveModal(null)} data-id="element-96">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative" onClick={e => e.stopPropagation()} data-id="element-97">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between" data-id="element-98">
              <h2 className="font-serif text-xl font-bold text-navy" data-id="element-99">
                {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close" data-id="element-100">
                <XIcon className="h-5 w-5 text-gray-500" data-id="element-101" />
              </button>
            </div>

            <div className="px-6 py-6 text-gray-700 text-sm leading-relaxed space-y-4" data-id="element-102">
              {activeModal === 'privacy' ? <PrivacyContent data-id="element-103" /> : <TermsContent data-id="element-104" />}
            </div>
          </div>
        </div>}
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-gray-400" data-id="element-105">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-106">
        Information We Collect
      </h3>
      <p data-id="element-107">
        When you purchase The Screen-Free Sanity Playbook, we collect your name
        and email address solely to deliver your digital product and send
        purchase confirmations.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-108">
        How We Use Your Information
      </h3>
      <p data-id="element-109">
        Your information is used exclusively to process your order, deliver the
        playbook, and send relevant product updates. We do not sell, rent, or
        share your personal information with third parties for marketing
        purposes.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-110">Cookies</h3>
      <p data-id="element-111">
        We may use cookies and similar technologies to improve your browsing
        experience and analyze site traffic. You can control cookie preferences
        through your browser settings.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-112">Data Security</h3>
      <p data-id="element-113">
        We implement industry-standard security measures to protect your
        personal information. All payment processing is handled through secure,
        PCI-compliant third-party processors.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-114">Your Rights</h3>
      <p data-id="element-115">
        You may request access to, correction of, or deletion of your personal
        data at any time. We will respond to such requests within a reasonable
        timeframe.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-116">
        Changes to This Policy
      </h3>
      <p data-id="element-117">
        We reserve the right to update this Privacy Policy at any time. Changes
        will be posted on this page with an updated revision date.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-gray-400" data-id="element-118">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-119">Agreement to Terms</h3>
      <p data-id="element-120">
        By purchasing and using The Screen-Free Sanity Playbook, you agree to be
        bound by these Terms of Service. If you do not agree, please do not
        purchase or use the product.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-121">
        Digital Product License
      </h3>
      <p data-id="element-122">
        Upon purchase, you are granted a personal, non-transferable,
        non-exclusive license to use The Screen-Free Sanity Playbook for
        personal and household use. You may not redistribute, resell, or share
        the playbook with others.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-123">
        Intellectual Property
      </h3>
      <p data-id="element-124">
        All content within The Screen-Free Sanity Playbook, including text,
        graphics, and design, is protected by copyright and intellectual
        property laws. Unauthorized reproduction or distribution is prohibited.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-125">Refund Policy</h3>
      <p data-id="element-126">
        Due to the digital nature of this product, all sales are final. If you
        experience any issues with your purchase, please reach out and we will
        do our best to resolve the matter.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-127">Disclaimer</h3>
      <p data-id="element-128">
        The Screen-Free Sanity Playbook is provided for informational and
        entertainment purposes. Activities are suggestions and should be
        supervised by a parent or guardian. We are not liable for any injury or
        damage resulting from the use of activities described in the playbook.
      </p>

      <h3 className="font-semibold text-navy text-base" data-id="element-129">Changes to Terms</h3>
      <p data-id="element-130">
        We reserve the right to modify these Terms of Service at any time.
        Continued use of the product after changes constitutes acceptance of the
        updated terms.
      </p>
    </>;
}